import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyStudyPlan = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMyPlan();
    }
  }, [user]);

  const fetchMyPlan = async () => {
    try {
      const response = await axios.get(`${API}/study-plan/my-plan`, { withCredentials: true });
      if (response.data.success && response.data.plan) {
        setPlan(response.data.plan);
      } else {
        setPlan(null);
      }
    } catch (error) {
      console.error('Error fetching plan:', error);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dayIndex) => {
    setExpandedDay(expandedDay === dayIndex ? null : dayIndex);
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      'VIDEO': '🎥',
      'READING': '📖',
      'PRACTICE_SET': '📝',
      'MIXED_TEST': '🧪',
      'SUMMARY_WRITING': '✍️'
    };
    return icons[type] || '📚';
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'EASY': 'bg-green-100 text-green-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'HARD': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Planım' : 'My Plan'} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-2">
            📅 {language === 'tr' ? 'Haftalık Çalışma Planım' : 'My Weekly Study Plan'}
          </h1>
          <p className="text-purple-100 text-lg">
            {language === 'tr' 
              ? 'Bu hafta için kişiselleştirilmiş çalışma programın'
              : 'Your personalized study schedule for this week'}
          </p>
        </div>

        {!plan ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {language === 'tr' ? 'Henüz Planın Yok' : 'No Plan Yet'}
            </h2>
            <p className="text-gray-600 mb-6">
              {language === 'tr' 
                ? 'Çalışma Planlayıcı\'dan yeni bir plan oluştur!'
                : 'Create a new plan from Study Planner!'}
            </p>
            <button
              onClick={() => navigate('/study-plan')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all font-semibold"
            >
              {language === 'tr' ? 'Plan Oluştur' : 'Create Plan'}
            </button>
          </div>
        ) : (
          <>
            {/* Plan Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                📊 {language === 'tr' ? 'Plan Özeti' : 'Plan Summary'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-blue-600">
                    {plan.weekly_time} {language === 'tr' ? 'saat' : 'hours'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'tr' ? 'Haftalık Zaman' : 'Weekly Time'}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">
                    {plan.days?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'tr' ? 'Gün' : 'Days'}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-purple-600">
                    {plan.days?.reduce((acc, day) => acc + (day.sessions?.length || 0), 0) || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {language === 'tr' ? 'Görev' : 'Tasks'}
                  </div>
                </div>
              </div>
              {plan.plan_summary && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <p className="text-gray-700">{plan.plan_summary}</p>
                </div>
              )}
            </div>

            {/* Daily Plan */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">
                📆 {language === 'tr' ? 'Günlük Program' : 'Daily Schedule'}
              </h2>
              
              {plan.days && plan.days.map((day, dayIndex) => (
                <div key={dayIndex} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {/* Day Header */}
                  <button
                    onClick={() => toggleDay(day.dayIndex)}
                    className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-lg">
                        {day.dayIndex}
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg font-bold text-gray-800">
                          {language === 'tr' ? `${day.dayIndex}. Gün` : `Day ${day.dayIndex}`}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {day.totalMinutes} {language === 'tr' ? 'dakika' : 'minutes'} • {day.sessions?.length || 0} {language === 'tr' ? 'görev' : 'tasks'}
                        </p>
                      </div>
                    </div>
                    <svg 
                      className={`w-6 h-6 text-gray-500 transition-transform ${expandedDay === day.dayIndex ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Sessions */}
                  {expandedDay === day.dayIndex && (
                    <div className="border-t border-gray-100 p-6 bg-gray-50">
                      <div className="space-y-4">
                        {day.sessions && day.sessions.map((session, sessionIndex) => (
                          <div key={sessionIndex} className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start space-x-3">
                                <span className="text-2xl">{getTaskTypeIcon(session.type)}</span>
                                <div>
                                  <h4 className="font-bold text-gray-800">{session.topic}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{session.reason}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(session.difficulty)}`}>
                                {session.difficulty}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                {session.type?.replace('_', ' ')}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                                ⏱️ {session.estimatedMinutes} min
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex justify-center space-x-4">
              <button
                onClick={() => navigate('/study-plan')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all font-semibold"
              >
                {language === 'tr' ? 'Yeni Plan Oluştur' : 'Create New Plan'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyStudyPlan;
